
import { useState, useEffect, useCallback } from 'react'
import {
  Box,
  SimpleGrid,
  Image,
  Text,
  IconButton,
  Checkbox,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Spinner,
  VStack,
  HStack,
  Button,
  Badge,
  useColorModeValue,
  Progress,
  CircularProgress,
  CircularProgressLabel,
} from '@chakra-ui/react'
import { Play, File, Music, FileText, Download, Trash2, Edit, X, Upload } from 'lucide-react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { MediaService } from '@/lib/services/mediaService'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { Pagination } from '@/components/tables/Pagination'
import type { Media, MediaListParams } from '@/types/media'
import type { MetaData } from '@/components/tables/types'

interface UploadingFile {
  id: string
  file: File
  progress: number
  status: 'uploading' | 'success' | 'error'
  error?: string
  preview?: string
}

interface MediaGridProps {
  params?: MediaListParams
  onSelect?: (media: Media) => void
  onEdit?: (media: Media) => void
  onDelete?: (media: Media) => void
  selectable?: boolean
  selectedIds?: number[]
  onSelectionChange?: (ids: number[]) => void
  onUploadComplete?: () => void
}

export function MediaGrid({
  params = {},
  onSelect,
  onEdit,
  onDelete,
  selectable = false,
  selectedIds = [],
  onSelectionChange,
  onUploadComplete,
}: MediaGridProps) {
  const colors = useThemeColors()
  const { showError, showSuccess } = useToastNotification()
  const [mediaItems, setMediaItems] = useState<Media[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedMedia, setSelectedMedia] = useState<Media | null>(null)
  const [metaData, setMetaData] = useState<MetaData | null>(null)
  const [nextPageUrl, setNextPageUrl] = useState<string | null>(null)
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const hoverBg = useColorModeValue('gray.50', 'gray.700')
  const dragBg = useColorModeValue('blue.50', 'blue.900')

  const fetchMedia = useCallback(async (url?: string | null, page?: number) => {
    setIsLoading(true)

    try {
      let response
      if (page) {
        // Fetch using page number directly
        response = await MediaService.getMedia({
          ...params,
          mode: 'grid',
          page,
        })
      } else if (url) {
        // Fetch using pagination URL
        try {
          const urlObj = new URL(url)
          const pageParam = urlObj.searchParams.get('page')
          const pageNum = pageParam ? parseInt(pageParam, 10) : 1
          
          // Extract all query params and merge with existing params
          const urlParams: Record<string, string> = {}
          urlObj.searchParams.forEach((value, key) => {
            urlParams[key] = value
          })
          
          response = await MediaService.getMedia({
            ...params,
            ...urlParams,
            mode: 'grid',
            page: pageNum,
          })
        } catch {
          // Fallback: try to extract page from URL string
          const pageMatch = url.match(/[?&]page=(\d+)/)
          const pageNum = pageMatch ? parseInt(pageMatch[1], 10) : 1
          response = await MediaService.getMedia({
            ...params,
            mode: 'grid',
            page: pageNum,
          })
        }
      } else {
        // Fetch first page
        response = await MediaService.getMedia({
          ...params,
          mode: 'grid',
          page: 1,
        })
      }

      setMediaItems(response.data)

      // Convert response to MetaData format for Pagination component
      let paginationLinks: Array<{ url: string | null; label: string; active: boolean }> = []
      
      // Check if links is already an array (Laravel pagination format)
      if (Array.isArray(response.links)) {
        paginationLinks = response.links.map((link: any) => ({
          url: link.url,
          label: typeof link.label === 'string' ? link.label.replace(/&[^;]+;/g, '') : String(link.label || ''),
          active: link.active || false,
        }))
      } else if (response.links && typeof response.links === 'object') {
        // Convert object format (first, last, prev, next) to array format
        // We need to build page numbers - check if we have last_page
        const lastPage = response.meta.last_page || 1
        const currentPage = response.meta.current_page || 1
        
        // Add Previous link
        if (response.links.prev) {
          paginationLinks.push({
            url: response.links.prev,
            label: 'Previous',
            active: false,
          })
        }
        
        // Add page number links (show max 7 pages around current)
        const maxPages = 7
        let startPage = Math.max(1, currentPage - Math.floor(maxPages / 2))
        let endPage = Math.min(lastPage, startPage + maxPages - 1)
        
        if (endPage - startPage < maxPages - 1) {
          startPage = Math.max(1, endPage - maxPages + 1)
        }
        
        // Add first page if not in range
        if (startPage > 1) {
          paginationLinks.push({
            url: response.links.first,
            label: '1',
            active: false,
          })
          if (startPage > 2) {
            paginationLinks.push({
              url: null,
              label: '...',
              active: false,
            })
          }
        }
        
        // Add page number links
        for (let i = startPage; i <= endPage; i++) {
          // Build URL for page number
          let url: string | null = null
          if (i === 1) {
            url = response.links.first
          } else if (i === lastPage) {
            url = response.links.last
          } else {
            // Construct URL from first page URL
            const baseUrl = response.links.first || ''
            if (baseUrl) {
              try {
                const urlObj = new URL(baseUrl)
                urlObj.searchParams.set('page', String(i))
                url = urlObj.toString()
              } catch {
                // Fallback: simple string replacement
                url = baseUrl.replace(/[?&]page=\d+/, `&page=${i}`).replace(/page=\d+/, `page=${i}`)
                if (!url.includes('page=')) {
                  url = baseUrl.includes('?') ? `${baseUrl}&page=${i}` : `${baseUrl}?page=${i}`
                }
              }
            }
          }
          
          paginationLinks.push({
            url,
            label: String(i),
            active: i === currentPage,
          })
        }
        
        // Add last page if not in range
        if (endPage < lastPage) {
          if (endPage < lastPage - 1) {
            paginationLinks.push({
              url: null,
              label: '...',
              active: false,
            })
          }
          paginationLinks.push({
            url: response.links.last,
            label: String(lastPage),
            active: false,
          })
        }
        
        // Add Next link
        if (response.links.next) {
          paginationLinks.push({
            url: response.links.next,
            label: 'Next',
            active: false,
          })
        }
      }

      setMetaData({
        total: response.meta.total,
        per_page: response.meta.per_page,
        current_page: response.meta.current_page,
        links: paginationLinks,
        last_page: response.meta.last_page ?? 0,
      })

      setNextPageUrl(response.links.next)
    } catch (error: any) {
      showError(error)
    } finally {
      setIsLoading(false)
    }
  }, [params, showError])

  useEffect(() => {
    fetchMedia()
  }, [fetchMedia])

  const handlePaginationClick = (url: string | null) => {
    if (url) {
      // Extract page number from URL
      try {
        const urlObj = new URL(url)
        const pageParam = urlObj.searchParams.get('page')
        const pageNum = pageParam ? parseInt(pageParam, 10) : 1
        fetchMedia(null, pageNum)
      } catch {
        // Fallback: try to extract page from URL string
        const pageMatch = url.match(/[?&]page=(\d+)/)
        const pageNum = pageMatch ? parseInt(pageMatch[1], 10) : 1
        fetchMedia(null, pageNum)
      }
      // Scroll to top of grid when changing pages
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  // Check if file is an image
  const isImageFile = (file: File): boolean => {
    if (file.type.startsWith('image/')) {
      return true
    }
    const fileName = file.name.toLowerCase()
    const imageExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp', '.svg', '.ico', '.tiff', '.tif']
    return imageExtensions.some(ext => fileName.endsWith(ext))
  }

  // Handle drag and drop
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!selectable) {
      setIsDragging(true)
    }
  }, [selectable])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    // Only set dragging to false if we're leaving the main container
    const relatedTarget = e.relatedTarget as HTMLElement
    if (!relatedTarget || !e.currentTarget.contains(relatedTarget)) {
      setIsDragging(false)
    }
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    if (selectable) {
      return
    }

    const files = Array.from(e.dataTransfer.files)
    if (files.length === 0) {
      return
    }

    // Process each file for upload
    const newUploadingFiles: UploadingFile[] = files.map((file) => {
      const id = `${Date.now()}-${Math.random()}`
      let preview: string | undefined
      
      if (isImageFile(file)) {
        preview = URL.createObjectURL(file)
      }

      return {
        id,
        file,
        progress: 0,
        status: 'uploading',
        preview,
      }
    })

    setUploadingFiles((prev) => [...prev, ...newUploadingFiles])

    // Upload each file individually with progress tracking
    for (const uploadingFile of newUploadingFiles) {
      try {
        await MediaService.uploadMediaFile(
          uploadingFile.file,
          (progress) => {
            setUploadingFiles((prev) =>
              prev.map((uf) =>
                uf.id === uploadingFile.id ? { ...uf, progress } : uf
              )
            )
          }
        )

        // Mark as success
        setUploadingFiles((prev) =>
          prev.map((uf) =>
            uf.id === uploadingFile.id
              ? { ...uf, status: 'success', progress: 100 }
              : uf
          )
        )

        // Clean up preview URL
        if (uploadingFile.preview) {
          URL.revokeObjectURL(uploadingFile.preview)
        }

        // Remove from uploading list after a short delay
        setTimeout(() => {
          setUploadingFiles((prev) => prev.filter((uf) => uf.id !== uploadingFile.id))
        }, 1000)

        // Refresh the grid
        fetchMedia()
        onUploadComplete?.()
      } catch (error: any) {
        // Mark as error
        setUploadingFiles((prev) =>
          prev.map((uf) =>
            uf.id === uploadingFile.id
              ? {
                  ...uf,
                  status: 'error',
                  error: error.message || 'Upload failed',
                }
              : uf
          )
        )

        // Clean up preview URL
        if (uploadingFile.preview) {
          URL.revokeObjectURL(uploadingFile.preview)
        }

        showError(error)
      }
    }
  }, [selectable, fetchMedia, onUploadComplete, showError])

  const handleItemClick = (media: Media) => {
    if (selectable) {
      const newSelectedIds = selectedIds.includes(media.id)
        ? selectedIds.filter((id) => id !== media.id)
        : [...selectedIds, media.id]
      onSelectionChange?.(newSelectedIds)
    } else {
      // If onSelect is provided, use it (parent handles the modal)
      // Otherwise, show internal modal
      if (onSelect) {
        onSelect(media)
      } else {
        setSelectedMedia(media)
        onOpen()
      }
    }
  }

  const handleCheckboxChange = (media: Media, isChecked: boolean) => {
    const newSelectedIds = isChecked
      ? [...selectedIds, media.id]
      : selectedIds.filter((id) => id !== media.id)
    onSelectionChange?.(newSelectedIds)
  }

  const handleDelete = (media: Media, e: React.MouseEvent) => {
    e.stopPropagation()
    // Use the onDelete callback if provided (parent handles confirmation modal)
    // Otherwise, use window.confirm as fallback
    if (onDelete) {
      onDelete(media)
    } else {
      // Fallback to window.confirm if no callback provided
      if (window.confirm('Are you sure you want to delete this media item?')) {
        handleDeleteConfirm(media)
      }
    }
  }

  const handleDeleteConfirm = async (media: Media) => {
    try {
      await MediaService.deleteMedia(media.id)
      showSuccess('Media deleted successfully')
      setMediaItems((prev) => prev.filter((item) => item.id !== media.id))
      onDelete?.(media)
    } catch (error: any) {
      showError(error)
    }
  }

  const handleEdit = (media: Media, e: React.MouseEvent) => {
    e.stopPropagation()
    onEdit?.(media)
  }

  const renderThumbnail = (media: Media) => {
    // Determine file type from multiple sources
    const fileType = (media.file_type || '').toLowerCase()
    const fileName = (media.file_name || '').toLowerCase()
    const fileExtension = fileName.split('.').pop() || ''
    
    // Check if it's an image (use is_image flag or check file type/extension)
    const isImage = media.is_image || 
      fileType.startsWith('image/') ||
      ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg', 'ico', 'tiff', 'tif'].includes(fileExtension)
    
    // Check if it's a video
    const isVideo = media.is_video || 
      fileType.startsWith('video/') ||
      ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv', 'm4v'].includes(fileExtension)
    
    // Check if it's audio
    const isAudio = media.is_audio || 
      fileType.startsWith('audio/') ||
      ['mp3', 'wav', 'ogg', 'aac', 'flac', 'm4a', 'wma'].includes(fileExtension)
    
    // Check if it's a document
    const isDocument = 
      fileType.startsWith('application/') || 
      fileType.startsWith('text/') ||
      ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv', 'rtf', 'odt', 'ods', 'odp'].includes(fileExtension)

    if (isImage) {
      return (
        <Image
          src={media.thumbnail_url || media.file_url}
          alt={media.file_name}
          objectFit="cover"
          w="100%"
          h="100%"
          fallback={
            <Box
              w="100%"
              h="100%"
              bg={colors.sectionBg}
              display="flex"
              alignItems="center"
              justifyContent="center"
              color={colors.textMuted}
            >
              <File size={32} />
            </Box>
          }
        />
      )
    }

    if (isVideo) {
      return (
        <Box
          w="100%"
          h="100%"
          bg={colors.sectionBg}
          display="flex"
          alignItems="center"
          justifyContent="center"
          position="relative"
          color={colors.textMuted}
        >
          <Play size={48} />
          <Badge
            position="absolute"
            bottom={2}
            right={2}
            colorScheme="red"
            fontSize="xs"
          >
            VIDEO
          </Badge>
        </Box>
      )
    }

    if (isAudio) {
      return (
        <Box
          w="100%"
          h="100%"
          bg={colors.sectionBg}
          display="flex"
          alignItems="center"
          justifyContent="center"
          color={colors.textMuted}
        >
          <Music size={48} />
        </Box>
      )
    }

    if (isDocument) {
      return (
        <Box
          w="100%"
          h="100%"
          bg={colors.sectionBg}
          display="flex"
          alignItems="center"
          justifyContent="center"
          color={colors.textMuted}
        >
          <FileText size={48} />
        </Box>
      )
    }

    // Default fallback for other file types
    return (
      <Box
        w="100%"
        h="100%"
        bg={colors.sectionBg}
        display="flex"
        alignItems="center"
        justifyContent="center"
        color={colors.textMuted}
      >
        <File size={48} />
      </Box>
    )
  }

  if (isLoading && mediaItems.length === 0 && uploadingFiles.length === 0) {
    return (
      <VStack spacing={4} py={8}>
        <Spinner size="xl" color="blue.500" />
        <Text color={colors.textMuted}>Loading media...</Text>
      </VStack>
    )
  }

  if (mediaItems.length === 0 && uploadingFiles.length === 0) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        h="100%"
        minH={0}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        position="relative"
      >
        {/* Drag overlay */}
        {isDragging && !selectable && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg={dragBg}
            border="3px"
            borderStyle="dashed"
            borderColor="blue.500"
            borderRadius="md"
            zIndex={10}
            display="flex"
            alignItems="center"
            justifyContent="center"
            pointerEvents="none"
          >
            <VStack spacing={4}>
              <Upload size={64} style={{ color: colors.text }} />
              <Text fontSize="lg" fontWeight="bold" color={colors.text}>
                Drop files to upload
              </Text>
              <Text fontSize="sm" color={colors.textMuted}>
                Release to start uploading
              </Text>
            </VStack>
          </Box>
        )}
        <VStack spacing={4} py={12}>
          <Text fontSize="lg" color={colors.textMuted}>
            No media items found
          </Text>
          {!selectable && (
            <Text fontSize="sm" color={colors.textMuted}>
              Drag and drop files here to upload
            </Text>
          )}
        </VStack>
      </Box>
    )
  }

  // Render uploading file item
  const renderUploadingItem = (uploadingFile: UploadingFile) => {
    const isImage = uploadingFile.preview && isImageFile(uploadingFile.file)
    
    return (
      <Box
        key={uploadingFile.id}
        position="relative"
        bg={colors.sectionBg}
        border="2px"
        borderColor={uploadingFile.status === 'error' ? 'red.500' : 'blue.500'}
        borderStyle="dashed"
        borderRadius="md"
        overflow="hidden"
        opacity={uploadingFile.status === 'success' ? 0.7 : 1}
      >
        {/* Thumbnail */}
        <Box
          position="relative"
          w="100%"
          aspectRatio="1"
          bg={colors.sectionBg}
          overflow="hidden"
        >
          {isImage && uploadingFile.preview ? (
            <Image
              src={uploadingFile.preview}
              alt={uploadingFile.file.name}
              objectFit="cover"
              w="100%"
              h="100%"
            />
          ) : (
            <Box
              w="100%"
              h="100%"
              bg={colors.sectionBg}
              display="flex"
              alignItems="center"
              justifyContent="center"
              color={colors.textMuted}
            >
              <File size={48} />
            </Box>
          )}
          
          {/* Progress Overlay */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="blackAlpha.600"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            gap={2}
          >
            {uploadingFile.status === 'uploading' && (
              <>
                <CircularProgress
                  value={uploadingFile.progress}
                  color="blue.400"
                  size="60px"
                  trackColor="whiteAlpha.300"
                >
                  <CircularProgressLabel color="white" fontSize="xs" fontWeight="bold">
                    {uploadingFile.progress}%
                  </CircularProgressLabel>
                </CircularProgress>
                <Text fontSize="xs" color="white" fontWeight="medium">
                  Uploading...
                </Text>
              </>
            )}
            {uploadingFile.status === 'success' && (
              <VStack spacing={1}>
                <Box
                  borderRadius="full"
                  bg="green.500"
                  p={2}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Upload size={24} style={{ color: 'white' }} />
                </Box>
                <Text fontSize="xs" color="white" fontWeight="medium">
                  Complete
                </Text>
              </VStack>
            )}
            {uploadingFile.status === 'error' && (
              <VStack spacing={1}>
                <Box
                  borderRadius="full"
                  bg="red.500"
                  p={2}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <X size={24} color="white" />
                </Box>
                <Text fontSize="xs" color="white" fontWeight="medium" textAlign="center" px={2}>
                  {uploadingFile.error || 'Failed'}
                </Text>
              </VStack>
            )}
          </Box>
        </Box>

        {/* File info */}
        <Box p={2} borderTop="1px" borderColor={colors.border}>
          <Text
            fontSize="xs"
            fontWeight="medium"
            color={colors.text}
            noOfLines={1}
            title={uploadingFile.file.name}
          >
            {uploadingFile.file.name}
          </Text>
          <Progress
            value={uploadingFile.progress}
            size="sm"
            colorScheme={uploadingFile.status === 'error' ? 'red' : 'blue'}
            mt={1}
            borderRadius="full"
          />
        </Box>
      </Box>
    )
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      h="100%"
      minH={0}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      position="relative"
    >
      {/* Drag overlay */}
      {isDragging && !selectable && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg={dragBg}
          border="3px"
          borderStyle="dashed"
          borderColor="blue.500"
          borderRadius="md"
          zIndex={10}
          display="flex"
          alignItems="center"
          justifyContent="center"
          pointerEvents="none"
        >
          <VStack spacing={4}>
            <Upload size={64} style={{ color: colors.text }} />
            <Text fontSize="lg" fontWeight="bold" color={colors.text}>
              Drop files to upload
            </Text>
            <Text fontSize="sm" color={colors.textMuted}>
              Release to start uploading
            </Text>
          </VStack>
        </Box>
      )}

      <Box flex="1" overflowY="auto" overflowX="hidden" pb={4}>
        <SimpleGrid columns={{ base: 2, sm: 3, md: 4, lg: 5, xl: 6 }} spacing={4}>
          {/* Uploading files */}
          {uploadingFiles.map((uploadingFile) => renderUploadingItem(uploadingFile))}
          
          {/* Media items */}
          {mediaItems.map((media) => (
          <Box
            key={media.id}
            position="relative"
            bg={colors.sectionBg}
            border="1px"
            borderColor={colors.border}
            borderRadius="md"
            overflow="hidden"
            cursor="pointer"
            transition="all 0.2s"
            _hover={{
              transform: 'translateY(-2px)',
              boxShadow: 'md',
              borderColor: 'blue.500',
              '& > .action-buttons': {
                opacity: 1,
              },
            }}
            onClick={() => handleItemClick(media)}
          >
            {/* Checkbox overlay for selectable mode */}
            {selectable && (
              <Box
                position="absolute"
                top={2}
                left={2}
                zIndex={2}
                onClick={(e) => e.stopPropagation()}
              >
                <Checkbox
                  isChecked={selectedIds.includes(media.id)}
                  onChange={(e) => handleCheckboxChange(media, e.target.checked)}
                  bg="white"
                  borderRadius="sm"
                />
              </Box>
            )}

            {/* Action buttons overlay */}
            {!selectable && (
              <Box
                className="action-buttons"
                position="absolute"
                top={2}
                right={2}
                zIndex={2}
                display="flex"
                gap={1}
                opacity={0}
                transition="opacity 0.2s"
              >
                {onEdit && (
                  <IconButton
                    aria-label="Edit"
                    icon={<Edit size={16} />}
                    size="sm"
                    colorScheme="blue"
                    onClick={(e) => handleEdit(media, e)}
                  />
                )}
                {onDelete && (
                  <IconButton
                    aria-label="Delete"
                    icon={<Trash2 size={16} />}
                    size="sm"
                    colorScheme="red"
                    onClick={(e) => handleDelete(media, e)}
                  />
                )}
              </Box>
            )}

            {/* Thumbnail */}
            <Box
              position="relative"
              w="100%"
              aspectRatio="1"
              bg={colors.sectionBg}
              overflow="hidden"
            >
              {renderThumbnail(media)}
            </Box>

            {/* File info */}
            <Box p={2} borderTop="1px" borderColor={colors.border}>
              <Text
                fontSize="xs"
                fontWeight="medium"
                color={colors.text}
                noOfLines={1}
                title={media.file_name}
              >
                {media.file_name}
              </Text>
              <Text fontSize="xs" color={colors.textMuted} mt={1}>
                {media.formatted_file_size || `${media.file_size} B`}
              </Text>
            </Box>
          </Box>
        ))}
        </SimpleGrid>
      </Box>

      {/* Pagination - Fixed at bottom */}
      {!isLoading && metaData && metaData.total > 0 && metaData.links && metaData.links.length > 0 && (
        <Box mt={4} px={2} flexShrink={0} borderTop="1px" borderColor={colors.border} pt={4}>
          <Pagination metas={metaData} onClick={handlePaginationClick} />
        </Box>
      )}

      {/* Media preview modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalBody p={0}>
            {selectedMedia && (
              <Box>
                {selectedMedia.is_image && (
                  <Image
                    src={selectedMedia.file_url}
                    alt={selectedMedia.file_name}
                    w="100%"
                    maxH="70vh"
                    objectFit="contain"
                  />
                )}
                {selectedMedia.is_video && (
                  <Box as="video" src={selectedMedia.file_url} controls w="100%" />
                )}
                {selectedMedia.is_audio && (
                  <Box as="audio" src={selectedMedia.file_url} controls w="100%" />
                )}
                <Box p={4}>
                  <Text fontWeight="bold" mb={2}>
                    {selectedMedia.file_name}
                  </Text>
                  <HStack spacing={4} fontSize="sm" color={colors.textMuted}>
                    <Text>{selectedMedia.formatted_file_size}</Text>
                    {selectedMedia.file_dimensions && (
                      <Text>{selectedMedia.file_dimensions}</Text>
                    )}
                    <Text>{selectedMedia.file_type}</Text>
                  </HStack>
                  {selectedMedia.caption && (
                    <Text mt={2} fontSize="sm">
                      {selectedMedia.caption}
                    </Text>
                  )}
                </Box>
              </Box>
            )}
          </ModalBody>
          </ModalContent>
        </Modal>
    </Box>
  )
}

